/**
 * Legacy URL redirect registry (Task #1730 — Tech Debt M).
 *
 * All non-conflicting alias-only `<Route>` registrations have been hoisted
 * out of `client/src/App.tsx` into this single function. The canonical-page
 * routes (everything that mounts a real component) and the small set of
 * aliases that MUST precede a more general dynamic route (e.g.
 * `/credentials/{bulk-revoke,batch-validate,kiosk}` before `/credentials/:id`)
 * still live inline in `App.tsx`.
 *
 * Why an array of `<Route>` elements (not a wrapping component): wouter's
 * `<Switch>` matches by `element.props.path` of its direct children. A
 * wrapping component has no `path` prop, so wouter would treat it as `*`
 * and swallow every otherwise-unmatched URL — breaking `NotFound`. The
 * exported function returns an array; React renders arrays as siblings,
 * and wouter's `flattenChildren` then iterates each `<Route>` element
 * directly, so each alias is matched as a discrete route.
 *
 * Each Route entry is intentionally written on a single line so the IA
 * validator (`scripts/validate-workforce-ia.ts`) — which scans for the
 * Route+path JSX form and the to attribute via regex — continues to
 * detect them. The validator was extended in this task to scan this
 * file in addition to App.tsx. The `path=` attribute MUST be the first
 * attribute on `<Route ...>` so the validator regex (which anchors on
 * `<Route\s+path=`) matches; `key=` follows path for the same reason.
 *
 * Lifecycle annotations (Task #2053 — UX Audit Phase 5):
 * Each redirect is annotated with either:
 *   @removal YYYY-QN — safe to remove once analytics confirm no traffic
 *   @keep <reason>   — permanent redirect, must not be removed
 *
 * Review cadence: evaluate @removal entries each quarter against analytics;
 * remove any that show zero traffic for 90+ consecutive days.
 */
import { Route } from "wouter";
import type { ReactElement } from "react";
import { AliasRedirect, PtwEquipmentPanelAlias } from "@/components/routing/AliasRedirect";

export function legacyRedirectRoutes(): ReactElement[] {
  return [
    /**
     * @keep permanent redirects — "contractor" was the legacy partner portal
     * path used in the original iOS app (v1.x). These URLs are bookmarked by
     * field supervisors and appear in PDF handout links distributed pre-2025.
     * The iOS app still opens these paths on older installs (minimum supported
     * version has not yet been retired). Do not remove until the iOS app
     * enforces a minimum version that uses the /partner/* paths.
     */
    <Route path="/contractor/dashboard" key="/contractor/dashboard">{() => <AliasRedirect from="/contractor/dashboard" to="/partner/dashboard" />}</Route>,
    <Route path="/contractor/workers" key="/contractor/workers">{() => <AliasRedirect from="/contractor/workers" to="/partner/workers" />}</Route>,
    <Route path="/contractor/upload" key="/contractor/upload">{() => <AliasRedirect from="/contractor/upload" to="/partner/upload" />}</Route>,
    <Route path="/contractor/certs" key="/contractor/certs">{() => <AliasRedirect from="/contractor/certs" to="/partner/certs" />}</Route>,
    <Route path="/contractor/jobs" key="/contractor/jobs">{() => <AliasRedirect from="/contractor/jobs" to="/partner/jobs" />}</Route>,
    <Route path="/contractor/attendance" key="/contractor/attendance">{() => <AliasRedirect from="/contractor/attendance" to="/partner/attendance" />}</Route>,
    <Route path="/contractor/timesheets" key="/contractor/timesheets">{() => <AliasRedirect from="/contractor/timesheets" to="/partner/timesheets" />}</Route>,

    /**
     * @removal 2026-Q4 — Revocation paths were consolidated into the unified
     * RevocationHub at /revocation (Task #1730). Analytics show <5 hits/week
     * as of 2026-Q1. Safe to remove when zero traffic confirmed for 90 days.
     */
    <Route path="/revocation-dashboard" key="/revocation-dashboard">{() => <AliasRedirect from="/revocation-dashboard" to="/revocation" />}</Route>,
    <Route path="/revocation-wizard" key="/revocation-wizard">{() => <AliasRedirect from="/revocation-wizard" to="/revocation?tab=wizard" />}</Route>,
    <Route path="/revocation-center" key="/revocation-center">{() => <AliasRedirect from="/revocation-center" to="/revocation?tab=center" />}</Route>,

    /**
     * @keep permanent redirects — /verify and /verifier are short URLs printed
     * on physical site badges and included in onboarding packs. They are
     * distributed to external verifiers (auditors, regulators) who may hold
     * printed copies for years. Do not remove.
     */
    <Route path="/verify" key="/verify">{() => <AliasRedirect from="/verify" to="/credentials/verify" />}</Route>,
    <Route path="/verifier" key="/verifier">{() => <AliasRedirect from="/verifier" to="/credentials/verify" />}</Route>,

    /**
     * @removal 2026-Q4 — /revocation-governance and /settings/revocation-governance
     * were pre-consolidation paths used in the settings sidebar. Replaced by
     * /revocation?tab=governance in Task #1632. Verify zero traffic before removing.
     */
    <Route path="/revocation-governance" key="/revocation-governance">{() => <AliasRedirect from="/revocation-governance" to="/revocation?tab=governance" />}</Route>,
    <Route path="/settings/revocation-governance" key="/settings/revocation-governance">{() => <AliasRedirect from="/settings/revocation-governance" to="/revocation?tab=governance" />}</Route>,

    /**
     * @removal 2026-Q4 — Workforce readiness sub-paths were collapsed into
     * tab-query params on /workforce/readiness in Task #1632 (Workforce v2).
     * These aliases exist for bookmarks and any external links from dashboards
     * created pre-migration. Safe to remove when zero traffic confirmed.
     */
    <Route path="/workforce/readiness-bundles" key="/workforce/readiness-bundles">{() => <AliasRedirect from="/workforce/readiness-bundles" to="/workforce/readiness?tab=bundles" />}</Route>,
    <Route path="/workforce/assignments" key="/workforce/assignments">{() => <AliasRedirect from="/workforce/assignments" to="/workforce/readiness?tab=assignments" />}</Route>,
    <Route path="/workforce/waivers" key="/workforce/waivers">{() => <AliasRedirect from="/workforce/waivers" to="/workforce/readiness?tab=waivers" />}</Route>,
    <Route path="/workforce/audit-log" key="/workforce/audit-log">{() => <AliasRedirect from="/workforce/audit-log" to="/workforce/readiness?tab=audit" />}</Route>,

    /**
     * @removal 2026-Q4 — Super-admin worker paths were restructured under the
     * SA Operate hub in Task #1632 Phase 2. These aliases support SA bookmarks
     * and deep-links in support tickets created before the restructure. Safe to
     * remove when zero SA traffic is confirmed for 90 days.
     */
    <Route path="/super-admin/workers/:id" key="/super-admin/workers/:id">{(params: { id: string }) => <AliasRedirect from="/super-admin/workers/:id" to={`/super-admin/operate/workforce/workers/${params.id}`} />}</Route>,
    <Route path="/super-admin/workers" key="/super-admin/workers">{() => <AliasRedirect from="/super-admin/workers" to="/super-admin/operate/workforce?tab=workers" />}</Route>,
    <Route path="/super-admin/worker-identity" key="/super-admin/worker-identity">{() => <AliasRedirect from="/super-admin/worker-identity" to="/super-admin/operate/workforce?tab=identity" />}</Route>,
    <Route path="/super-admin/marketplace" key="/super-admin/marketplace">{() => <AliasRedirect from="/super-admin/marketplace" to="/super-admin/operate/workforce?tab=marketplace" />}</Route>,
    <Route path="/super-admin/relationship-oversight" key="/super-admin/relationship-oversight">{() => <AliasRedirect from="/super-admin/relationship-oversight" to="/super-admin/operate/workforce?tab=relationships" />}</Route>,
    <Route path="/super-admin/workforce-compliance" key="/super-admin/workforce-compliance">{() => <AliasRedirect from="/super-admin/workforce-compliance" to="/super-admin/operate/workforce?tab=compliance" />}</Route>,

    /**
     * @removal 2026-Q4 — Worker task sub-paths were consolidated into a
     * tabbed view at /worker/tasks in Task #1730. Low residual traffic expected
     * from cached PWA shells on older devices. Monitor and remove when clear.
     */
    <Route path="/worker/muster-response" key="/worker/muster-response">{() => <AliasRedirect from="/worker/muster-response" to="/worker/tasks?tab=muster" />}</Route>,
    <Route path="/worker/muster" key="/worker/muster">{() => <AliasRedirect from="/worker/muster" to="/worker/tasks?tab=muster" />}</Route>,
    <Route path="/worker/shifts" key="/worker/shifts">{() => <AliasRedirect from="/worker/shifts" to="/worker/tasks?tab=shifts" />}</Route>,

    /**
     * @removal 2026-Q4 — Onboarding top-level paths were moved under the
     * workforce roster hub (/workforce/roster/onboarding) in Task #1632.
     * Deep-links in email invitation templates still use these paths for
     * tenants on the legacy email template set; monitor after template
     * migration rollout completes in 2026-Q3.
     */
    <Route path="/onboarding/intake" key="/onboarding/intake">{() => <AliasRedirect from="/onboarding/intake" to="/workforce/roster/onboarding/new" />}</Route>,
    <Route path="/onboarding/analytics" key="/onboarding/analytics">{() => <AliasRedirect from="/onboarding/analytics" to="/workforce/roster/onboarding?tab=analytics" />}</Route>,
    <Route path="/onboarding/invitations" key="/onboarding/invitations">{() => <AliasRedirect from="/onboarding/invitations" to="/workforce/roster/onboarding?tab=invitations" />}</Route>,
    <Route path="/onboarding/bulk-import" key="/onboarding/bulk-import">{() => <AliasRedirect from="/onboarding/bulk-import" to="/workforce/roster/onboarding?tab=import" />}</Route>,
    <Route path="/onboarding/:id" key="/onboarding/:id">{(params: { id: string }) => <AliasRedirect from="/onboarding/:id" to={`/workforce/roster/onboarding/${params.id}`} />}</Route>,
    <Route path="/onboarding" key="/onboarding">{() => <AliasRedirect from="/onboarding" to="/workforce/roster/onboarding" />}</Route>,

    /**
     * @removal 2026-Q4 — Operations sub-paths were converted from standalone
     * routes to tab-query params on /workforce/operations in Task #1632 Phase 1.
     * These aliases support the legacy sidebar bookmarks in the desktop app
     * and any saved dashboard links. Safe to remove when analytics confirm
     * zero traffic for 90 days post-sidebar migration completion.
     */
    <Route path="/operations/headcount" key="/operations/headcount">{() => <AliasRedirect from="/operations/headcount" to="/workforce/operations?tab=headcount" />}</Route>,
    <Route path="/operations/situational-map" key="/operations/situational-map">{() => <AliasRedirect from="/operations/situational-map" to="/workforce/operations?tab=map" />}</Route>,
    <Route path="/operations/dashboard" key="/operations/dashboard">{() => <AliasRedirect from="/operations/dashboard" to="/workforce/operations" />}</Route>,
    <Route path="/operations/deployments" key="/operations/deployments">{() => <AliasRedirect from="/operations/deployments" to="/workforce/operations?tab=deployments" />}</Route>,
    <Route path="/operations/lifecycle" key="/operations/lifecycle">{() => <AliasRedirect from="/operations/lifecycle" to="/workforce/operations?tab=lifecycle" />}</Route>,
    <Route path="/operations/transfers" key="/operations/transfers">{() => <AliasRedirect from="/operations/transfers" to="/workforce/operations?tab=transfers" />}</Route>,
    <Route path="/operations/suspensions" key="/operations/suspensions">{() => <AliasRedirect from="/operations/suspensions" to="/workforce/operations?tab=suspensions" />}</Route>,
    <Route path="/operations/demobilizations" key="/operations/demobilizations">{() => <AliasRedirect from="/operations/demobilizations" to="/workforce/operations?tab=demobilization" />}</Route>,
    <Route path="/operations/offboarding" key="/operations/offboarding">{() => <AliasRedirect from="/operations/offboarding" to="/workforce/operations?tab=offboarding" />}</Route>,

    /**
     * @removal 2026-Q4 — PTW standalone route aliases were absorbed into the
     * Unified Permit Console (Task #1266, PTW UXS-1). The /ptw-workflows and
     * /settings/ptw-workflows paths appear in PDF export templates and
     * regulator audit pack links; review against template version history
     * before removing. /settings/ptw-pdf-defaults is referenced in the SA
     * configuration docs — coordinate removal with docs update.
     * Updated (Task #3310): both aliases now point to /ptw/library (the unified
     * PTW Templates page) instead of /ptw/configure?tab=templates.
     */
    <Route path="/ptw-workflows" key="/ptw-workflows">{() => <AliasRedirect from="/ptw-workflows" to="/ptw/library" />}</Route>,
    <Route path="/settings/ptw-workflows" key="/settings/ptw-workflows">{() => <AliasRedirect from="/settings/ptw-workflows" to="/ptw/library" />}</Route>,
    <Route path="/settings/ptw-pdf-defaults" key="/settings/ptw-pdf-defaults">{() => <AliasRedirect from="/settings/ptw-pdf-defaults" to="/ptw/configure?tab=settings&section=pdf" />}</Route>,

    /**
     * Task #2314 — /supervisor/shift-handover is now a real dedicated page
     * registered in workerRoutes.tsx. The previous redirect (→ /ptw?drawer=handover)
     * is removed; the page itself handles /new and /:id sub-routes.
     *
     * Legacy supervisor tab routes redirect to the unified Supervisor Hub.
     * @keep permanent redirect — URLs appear in notification emails and mobile push.
     */
    <Route path="/supervisor/crew-attendance" key="/supervisor/crew-attendance">{() => <AliasRedirect from="/supervisor/crew-attendance" to="/supervisor/today?tab=attendance" />}</Route>,
    <Route path="/supervisor/exceptions" key="/supervisor/exceptions">{() => <AliasRedirect from="/supervisor/exceptions" to="/supervisor/today?tab=exceptions" />}</Route>,
    <Route path="/supervisor/timesheet-approval" key="/supervisor/timesheet-approval">{() => <AliasRedirect from="/supervisor/timesheet-approval" to="/supervisor/today?tab=timesheets" />}</Route>,

    /**
     * @keep permanent redirect — /equipment/ptw-panel is the deep-link target
     * used by the QR-scan flow on equipment kiosk tablets. Changing this path
     * would require a firmware OTA push to all deployed kiosks. Keep until
     * kiosk fleet is migrated to dynamic QR resolve (planned 2027-Q1).
     */
    <Route path="/equipment/ptw-panel" key="/equipment/ptw-panel">{() => <PtwEquipmentPanelAlias />}</Route>,
  ];
}
